/**
 * CONFLICTS REQUEST PAGE — admin-editable copy and options.
 *
 * The standalone /conflicts page (pages/ConflictsStandalone.jsx) is a deliberately
 * chrome-free surface for requesting a conflicts review without opening a matter. Every
 * word on it used to be hardcoded; it now reads `settings/conflictsPage`, edited from
 * Admin → Conflicts page.
 *
 * Pure module: defaults + normalization only, so both the page and its editor agree on
 * the shape and a missing/partial/malformed document can never break the page. A firm
 * that never opens the editor sees exactly the original copy.
 */

import { sanitizeSubFieldRows } from './fieldLayout.js';

/** Every party row always has this identifier column; it is never part of the editable
 * `partyFields` list and can never be removed or renamed by an admin. */
export const NAME_PARTY_FIELD = { path: 'name', label: 'Name', type: 'text' };

export const DEFAULT_CONFLICTS_PAGE = {
  eyebrow: 'AALRR',
  title: 'Conflict check request',
  intro:
    'Submit the names and entities that need a conflicts review. This request is separate from new-matter intake and is sent directly to the Conflicts team.',

  requestingPartyTitle: 'Requesting party',
  requestingPartyHelp: 'These details are taken from your signed-in AALRR account.',

  partiesTitle: 'Names and entities',
  partiesHelp: 'Include each individual or organization that should be reviewed.',
  partiesValidationMessage: 'Add at least one name or entity before submitting.',

  descriptionEnabled: true,
  descriptionRequired: false,
  descriptionLabel: 'Matter description',
  descriptionHelp: 'Briefly describe the prospective matter so Conflicts has useful context.',
  descriptionValidationMessage: 'Add a matter description before submitting.',

  subjectLabel: 'Email subject line',
  subjectHelp:
    "Added to the end of the email's subject, for example the plaintiff's last name, so the request is easy to find.",

  submitLabel: 'Submit conflict check',
  submittedTitle: 'Request submitted',
  submittedBody:
    'The Conflicts team has received your request. You may close this page or submit another request below.',
  submitAnotherLabel: 'Submit another request',
};

/** Extra columns beyond the mandatory Name column. Default: none — "just a list of
 * names" is the shipped shape; admins add columns (entity type, role, ...) from
 * Admin -> Conflicts page, using the same column editor as a repeating group. */
export const DEFAULT_PARTY_FIELDS = [];

/** String fields fall back to the default when blank/missing; booleans to their default. */
const BOOLEAN_KEYS = ['descriptionEnabled', 'descriptionRequired'];

/**
 * Tolerates a missing, partial or malformed document. A blank string means "use the
 * default" rather than rendering an empty heading — an admin who clears a field is
 * resetting it, not trying to ship a page with no title.
 */
export function normalizeConflictsPage(stored) {
  const source = stored && typeof stored === 'object' ? stored : {};
  const result = {};
  for (const [key, fallback] of Object.entries(DEFAULT_CONFLICTS_PAGE)) {
    if (BOOLEAN_KEYS.includes(key)) {
      result[key] = typeof source[key] === 'boolean' ? source[key] : fallback;
      continue;
    }
    const value = typeof source[key] === 'string' ? source[key].trim() : '';
    result[key] = value || fallback;
  }
  // 'name' is reserved for the mandatory identifier column above and is dropped here so an
  // admin-added column can never collide with it in a stored row.
  result.partyFields = sanitizeSubFieldRows(source.partyFields).filter((f) => f.path !== 'name');
  return result;
}

/** The full column list for the party-list Repeatable control: the mandatory Name column
 * followed by whatever extra columns an admin has configured. */
export function conflictsPartyFields(page) {
  return [NAME_PARTY_FIELD, ...(Array.isArray(page?.partyFields) ? page.partyFields : [])];
}

/**
 * Validates a submission against the configured options.
 * @returns {string} an error message, or '' when the submission may proceed.
 */
export function validateConflictsSubmission(settings, { parties, description }) {
  const config = normalizeConflictsPage(settings);
  const named = Array.isArray(parties)
    ? parties.some((party) => typeof party?.name === 'string' && party.name.trim())
    : false;
  if (!named) return config.partiesValidationMessage;
  if (config.descriptionEnabled && config.descriptionRequired && !String(description || '').trim()) {
    return config.descriptionValidationMessage;
  }
  return '';
}

/**
 * The email subject of a request, for the preview under the "Email subject line" box.
 *
 * A copy of functions/src/conflicts/subject.js, which is what actually builds the subject the
 * Conflicts team receives. conflictsPageSettings.test.js holds the two to identical output, so the
 * preview can never promise a subject the server does not send.
 */
export const SUBJECT_SUFFIX_MAX = 100;

export function cleanSubjectSuffix(raw) {
  return String(raw === undefined || raw === null ? '' : raw)
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, SUBJECT_SUFFIX_MAX)
    .trim();
}

export function conflictCheckSubject({ count, submitterName, suffix }) {
  const n = Number(count) || 0;
  const base = `[Conflict Check] ${n} ${n === 1 ? 'party' : 'parties'} submitted by ${submitterName}`;
  const note = cleanSubjectSuffix(suffix);
  return note ? `${base} - ${note}` : base;
}
